import { getApiBaseUrl } from '@/app/runtime';
import i18n from '@/i18n';
import { resolveAuthenticatedLandingHash } from '@/utils/authRedirect';
import { redirectToVerifyPhoneNumber } from '@/utils/redirectToVerifyPhoneNumber';
import axios from 'axios';
import normalizeBaseDomain from './normalizeBaseDomain';
import { isOnLoginRoute as isOnLoginRouteShared, refreshAccessToken } from './sharedTokenRefresh';
import { notifyInsufficientTokens } from './tokenErrors';

const authMethod = process.env.VUE_APP_AUTH_METHOD || 'direct';
const baseDomain =
  authMethod === 'direct'
    ? normalizeBaseDomain(process.env.VUE_APP_SCRUMBLE_API_URL)
    : normalizeBaseDomain(getApiBaseUrl());
const baseURL =
  authMethod === 'direct'
    ? `${baseDomain}/api/v1`
    : baseDomain.endsWith('/api/v2')
    ? `${baseDomain}/scrumble`
    : `${baseDomain}/api/v2/scrumble`;

function isOnLoginRoute() {
  const hash = window.location.hash || '';
  const pathname = window.location.pathname || '';
  return hash.startsWith('#/login') || pathname === '/login';
}

function redirectToHomeIfOnLogin() {
  if (isOnLoginRoute()) {
    window.location.assign(resolveAuthenticatedLandingHash());
  }
}

const request = axios.create({ baseURL });

request.interceptors.request.use(
  (config) => {
    const accessToken = window.sessionStorage.getItem('accessToken');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    config.headers['Accept-Language'] = i18n.global.locale.value;

    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const status = response?.status ?? 200;

    notifyInsufficientTokens(response, config);

    if (status === 403 && error.response.data.error === 'phone_verification_required') {
      // Only redirect if we're sure the user data is loaded
      // This prevents the dialog from showing before we know if phone is already verified
      const isUserLoaded = window.sessionStorage.getItem('isUserLoaded') === 'true';
      const isAuthenticated = window.sessionStorage.getItem('accessToken');

      if (isAuthenticated && isUserLoaded) {
        redirectToVerifyPhoneNumber();
      }

      return Promise.reject(error);
    }

    if (status === 401 && !config._retry) {
      if (isOnLoginRouteShared()) {
        return Promise.reject(error);
      }
      config._retry = true;
      try {
        // SHARED refresh — every axios client joins the same in-flight
        // refresh in sharedTokenRefresh.js to avoid the rotating-refresh-
        // token race that used to trigger session_expired redirect loops.
        const accessToken = await refreshAccessToken({ rejectedAccessToken: config.headers.Authorization?.replace(/^Bearer /, '') });
        config.headers.Authorization = `Bearer ${accessToken}`;
        redirectToHomeIfOnLogin();
        return request(config);
      } catch (err) {
        // `refreshAccessToken` already wiped the stale tokens. We deliberately
        // don't hard-redirect here — the Client/AuthenticatedClient 401 paths
        // do that; Scrumble-side 401s often coincide with one of those and
        // the redirect would be a double-trigger. Reject and let the higher
        // layers decide.
      }
    }

    return Promise.reject(error);
  }
);

export default request;
